import { motion } from 'framer-motion';
import {
    ArrowRight,
    Building2,
    Clock,
    DollarSign,
    ExternalLink,
    GraduationCap,
    Heart,
    Landmark,
    TrendingUp,
    Users
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const CaseStudies = () => {
    const caseStudies = [
        {
            id: 'healthcare-system',
            title: 'Regional Healthcare System Streamlines Patient Records',
            company: 'Regional Healthcare Network',
            industry: 'Healthcare',
            icon: Heart,
            summary: 'Implemented patient-controlled digital health records, reducing verification time by 85% while ensuring HIPAA compliance.',
            challenge: 'Manual verification of patient records across multiple facilities was time-consuming, costing $150 per verification and creating delays in patient care.',
            solution: 'Deployed Self-Sovereign Identity solution enabling patients to control verifiable health credentials, reducing verification costs by 90%.',
            results: [
                { metric: '85%', description: 'Reduction in record verification time' },
                { metric: '40+', description: 'Facilities connected' },
                { metric: '100%', description: 'HIPAA compliance maintained' },
                { metric: '$2.3M', description: 'Annual cost savings from SSI implementation' }
            ],
            testimonial: {
                quote: "Self-sovereign identity technology completely transformed how we handle patient records. Our patients now have complete control over their health data while enabling seamless, secure sharing across our entire network.",
                author: "Chief Information Officer",
                role: "Regional Healthcare Network"
            },
            tags: ['Healthcare', 'HIPAA', 'Patient Records', 'Interoperability']
        },
        {
            id: 'financial-kyc',
            title: 'Global Bank Revolutionizes Customer Onboarding',
            company: 'Global Financial Institution',
            industry: 'Financial Services',
            icon: Landmark,
            summary: 'Reduced customer onboarding time from 5 days to 30 minutes while improving fraud detection by 60%.',
            challenge: 'Traditional KYC processes cost $500 per customer, took 5-7 days, and created friction for customers trying to open accounts.',
            solution: 'Implemented SSI-powered identity verification, reducing KYC costs to $50 per customer through reusable verifiable credentials.',
            results: [
                { metric: '30min', description: 'Average onboarding time' },
                { metric: '60%', description: 'Improvement in fraud detection' },
                { metric: '95%', description: 'Customer satisfaction score' },
                { metric: '$4.7M', description: 'Annual savings from SSI-powered KYC automation' }
            ],
            testimonial: {
                quote: "The transformation in our customer experience has been absolutely remarkable. We've gone from days to minutes for account opening while dramatically improving our security posture and fraud detection capabilities.",
                author: "Head of Digital Banking",
                role: "Global Financial Institution"
            },
            tags: ['KYC', 'Banking', 'Fraud Prevention', 'Customer Experience']
        },
        {
            id: 'university-credentials',
            title: 'Top University Issues Tamper-Proof Digital Diplomas',
            company: 'Major University System',
            industry: 'Education',
            icon: GraduationCap,
            summary: 'Eliminated diploma fraud and reduced verification time for employers from weeks to seconds.',
            challenge: 'Diploma fraud cost employers $75M annually, while manual verification processes took 3-5 days at $40 per verification.',
            solution: 'Issued blockchain-backed verifiable credentials reducing verification time to seconds and costs to $2 per verification.',
            results: [
                { metric: '100%', description: 'Fraud elimination rate' },
                { metric: '30,000+', description: 'Digital diplomas issued' },
                { metric: '5sec', description: 'Average verification time' },
                { metric: '500+', description: 'Employer partners using verification' }
            ],
            testimonial: {
                quote: "Our graduates now have instant, cryptographically verifiable proof of their achievements. Employers love the instant verification capabilities, and we've completely eliminated diploma fraud across our entire network.",
                author: "University Registrar",
                role: "Major University System"
            },
            tags: ['Education', 'Diplomas', 'Fraud Prevention', 'Instant Verification']
        },
        {
            id: 'enterprise-workforce',
            title: 'Fortune 500 Company Secures Global Workforce',
            company: 'Fortune 500 Technology Enterprise',
            industry: 'Technology',
            icon: Building2,
            summary: 'Managed credentials for 50,000+ employees across 40 countries, improving security and reducing administrative overhead.',
            challenge: 'Managing employee credentials cost $300 per employee annually, with security incidents costing $2M per year across global operations.',
            solution: 'Deployed enterprise SSI solution reducing credential management costs to $90 per employee while eliminating security incidents.',
            results: [
                { metric: '50,000+', description: 'Employees with digital credentials' },
                { metric: '40', description: 'Countries with unified identity system' },
                { metric: '70%', description: 'Reduction in identity-related incidents' },
                { metric: '$1.8M', description: 'Annual savings from decentralized identity management' }
            ],
            testimonial: {
                quote: "Self-sovereign identity technology gave us a unified, highly secure way to manage our global workforce identity. The security improvements and cost savings far exceeded our most optimistic expectations.",
                author: "Chief Security Officer",
                role: "Fortune 500 Technology Enterprise"
            },
            tags: ['Enterprise', 'Global Workforce', 'Access Control', 'Security']
        }
    ];

    const industries = [
        { name: 'Healthcare', count: 8, icon: Heart },
        { name: 'Financial Services', count: 12, icon: Landmark },
        { name: 'Education', count: 15, icon: GraduationCap },
        { name: 'Enterprise', count: 25, icon: Building2 }
    ];

    const caseStudiesStructuredData = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Self-Sovereign Identity Case Studies",
        "description": "Real-world success stories of organizations implementing SSI technology for healthcare, finance, education, and enterprise use cases.",
        "url": "https://documint.id/case-studies",
        "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": caseStudies.length,
            "itemListElement": caseStudies.map((study, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                    "@type": "CaseStudy",
                    "name": study.title,
                    "description": study.summary,
                    "industry": study.industry
                }
            }))
        }
    };

    return (
        <div className="min-h-screen pt-20">
            <SEOHead 
                title="SSI Case Studies - Real Success Stories"
                description="Discover how healthcare, finance, education & enterprise organizations achieve 85% time reduction and $8.8M savings with Self-Sovereign Identity solutions."
                keywords="SSI case studies, verifiable credentials success stories, digital identity implementation, healthcare SSI, financial KYC, education diplomas, enterprise credentials, identity verification results"
                url="https://documint.id/case-studies"
                structuredData={caseStudiesStructuredData}
            />
            {/* Hero Section */}
            <section className="bg-surface-100 py-20">
                <div className="container-custom text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                            Customer
                            <span className="text-neon-300"> Success Stories</span>
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                            Explore how forward-thinking organizations across industries are revolutionizing their operations 
                            with Self-Sovereign Identity technology, achieving remarkable cost savings and efficiency gains.
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                            {industries.map((industry, index) => (
                                <motion.div
                                    key={industry.name}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    className="glass p-4 rounded-lg text-center"
                                >
                                    <industry.icon className="w-6 h-6 text-neon-300 mx-auto mb-2" />
                                    <div className="text-sm font-medium text-white">{industry.name}</div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Case Studies Grid */}
            <section className="py-20 bg-white">
                <div className="container-custom">
                    <div className="space-y-16">
                        {caseStudies.map((study, index) => (
                            <motion.div
                                key={study.id}
                                initial={{ opacity: 0, y: 40 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                className="bg-gray-50 rounded-2xl overflow-hidden"
                            >
                                {/* Header */}
                                <div className="bg-gradient-to-r from-primary-600 to-neon-600 p-8 text-white">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-start space-x-4">
                                            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                                                <study.icon className="w-6 h-6" />
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium opacity-90 mb-1">
                                                    {study.industry}
                                                </div>
                                                <h2 className="text-2xl font-bold mb-2">
                                                    {study.title}
                                                </h2>
                                                <div className="text-lg opacity-90">
                                                    {study.company}
                                                </div>
                                            </div>
                                        </div>
                                        <ExternalLink className="w-5 h-5 opacity-70" />
                                    </div>
                                    <p className="text-white/90 mt-4 text-lg">
                                        {study.summary}
                                    </p>
                                </div>

                                {/* Content */}
                                <div className="p-8 bg-gradient-to-br from-blue-50 via-indigo-100 to-purple-200 backdrop-blur-sm border-t border-indigo-200/50">
                                    <div className="grid lg:grid-cols-3 gap-8">
                                        {/* Challenge & Solution */}
                                        <div className="lg:col-span-2 space-y-6">
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                                    The Challenge
                                                </h3>
                                                <p className="text-gray-700">
                                                    {study.challenge}
                                                </p>
                                            </div>
                                            
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                                    The Solution
                                                </h3>
                                                <p className="text-gray-700">
                                                    {study.solution}
                                                </p>
                                            </div>

                                            {/* Testimonial */}
                                            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-indigo-200 shadow-lg">
                                                <blockquote className="text-gray-700 italic mb-4">
                                                    &ldquo;{study.testimonial.quote}&rdquo;
                                                </blockquote>
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-neon-500 rounded-full flex items-center justify-center">
                                                        <Users className="w-5 h-5 text-white" />
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-gray-800">
                                                            {study.testimonial.author}
                                                        </div>
                                                        <div className="text-sm text-gray-600">
                                                            {study.testimonial.role}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Results */}
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                                Key Results
                                            </h3>
                                            <div className="space-y-4">
                                                {study.results.map((result, resultIndex) => (
                                                    <div key={resultIndex} className="bg-white/70 backdrop-blur-sm rounded-lg p-4 border border-indigo-200 shadow-md">
                                                        <div className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-neon-600 bg-clip-text text-transparent mb-1">
                                                            {result.metric}
                                                        </div>
                                                        <div className="text-sm text-gray-600">
                                                            {result.description}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Tags */}
                                            <div className="mt-6">
                                                <div className="text-sm font-medium text-gray-700 mb-2">
                                                    Technologies Used
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {study.tags.map((tag, tagIndex) => (
                                                        <span
                                                            key={tagIndex}
                                                            className="px-3 py-1 bg-gradient-to-r from-primary-100 to-neon-100 text-primary-700 rounded-full text-xs font-medium border border-primary-200 shadow-sm"
                                                        >
                                                            {tag}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-20 bg-surface-100">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                            Transformative Results Across Industries
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Organizations implementing Self-Sovereign Identity consistently achieve breakthrough improvements in efficiency, security, and cost optimization.
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            { icon: Clock, metric: '90%', description: 'Average time reduction in identity verification processes using SSI' },
                            { icon: DollarSign, metric: '$8.8M', description: 'Total cost savings achieved across all SSI implementations' },
                            { icon: TrendingUp, metric: '99.8%', description: 'Fraud prevention rate with cryptographic verification' },
                            { icon: Users, metric: '96%', description: 'User satisfaction improvement with self-sovereign identity' }
                        ].map((stat, index) => (
                            <motion.div
                                key={stat.description}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="glass p-6 rounded-xl text-center"
                            >
                                <div className="w-12 h-12 bg-primary-600/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                                    <stat.icon className="w-6 h-6 text-neon-300" />
                                </div>
                                <div className="text-3xl font-bold bg-gradient-to-r from-primary-400 to-neon-400 bg-clip-text text-transparent mb-2">
                                    {stat.metric}
                                </div>
                                <div className="text-gray-300 text-sm">
                                    {stat.description}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-white">
                <div className="container-custom text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                            Ready to Transform Your Operations?
                        </h2>
                        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                            Join forward-thinking organizations that have revolutionized their identity infrastructure with Self-Sovereign Identity technology. 
                            Discover how you can achieve similar breakthrough results.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/contact" className="btn-primary inline-flex items-center">
                                Start Your Project
                                <ArrowRight size={20} className="ml-2" />
                            </Link>
                            <Link to="/pricing" className="btn-secondary inline-flex items-center">
                                View Pricing
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default CaseStudies;